import { Alert, Box, styled } from "@mui/material";
import { ReactElement } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import NumberStat from "~frontend/components/stats/NumberStat";

import { StatsQueryResult } from "./useCampaignStats";

const StatWrapper = styled(Box)({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "center",
});

export default function TopLineStats({
  stats,
  showClicks,
  showFundraisingFields,
  usingShortCodeStrategy,
}: {
  stats: StatsQueryResult;
  showClicks: boolean;
  showFundraisingFields: boolean;
  usingShortCodeStrategy: boolean;
}): ReactElement {
  if (stats.state === "loading") {
    return <Loading />;
  }

  if (stats.state === "error") {
    return <Alert severity="error">Error loading campaign stats</Alert>;
  }

  const data = stats.data;

  function pctStat(
    label: string,
    numerator: number | undefined,
    denominator: number | undefined
  ): ReactElement {
    return (
      <NumberStat
        format="percent"
        value={
          numerator && denominator && denominator > 0
            ? numerator / denominator
            : 0
        }
        sx={{ mx: 1 }}
      >
        {label}
      </NumberStat>
    );
  }

  const replyRate = pctStat(
    "Reply Rate",
    data.contactsDidReplyCount,
    data.contactCount
  );

  let filterCount = data.initialMessageFilterCount;
  if (
    data.initialMessageMaybeFilterCount >
    data.contactCount * data.maybeFilteredThreshold
  ) {
    // A lot (more than the threshold) of contact received a "maybe filtering"
    // error. These errors typically mean filtering if we see a *lot* of them,
    // but can also be due to other reasons if we only see a few. In this case,
    // we saw a lot so we're going to consider them filtering.

    filterCount += data.initialMessageMaybeFilterCount;
  }
  const filterRate = pctStat("Filter Rate", filterCount, data.contactCount);

  const optOutRate = pctStat(
    usingShortCodeStrategy ? "Unsubscribe Rate" : "Opt Out Rate",
    data.contactsOptedOutCount,
    data.contactCount
  );

  const totalRaised = (
    <NumberStat
      value={(data.totalRaisedInCents || 0) / 100.0}
      format="usd"
      sx={{ mx: 1 }}
    >
      Total Raised
    </NumberStat>
  );

  const initialCostInCents = data.initialCostInMillicents / 1000;
  const estimatedROI = pctStat(
    "Estimated ROI",
    data.totalRaisedInCents,
    initialCostInCents
  );

  const clickRate = showClicks ? (
    pctStat("Click Rate", data.clickCount, data.contactCount)
  ) : (
    <NumberStat
      format="number"
      disabled
      disabledMessage="Enable link shortening to see click rate"
      value={0}
      sx={{ mx: 1 }}
    >
      Click Rate
    </NumberStat>
  );

  if (showFundraisingFields) {
    return (
      <>
        <FlexBox>
          <StatWrapper>{replyRate}</StatWrapper>
          <StatWrapper>{filterRate}</StatWrapper>
          <StatWrapper>{optOutRate}</StatWrapper>
        </FlexBox>
        <FlexBox justifyContent="space-between">
          <StatWrapper>{totalRaised}</StatWrapper>
          <StatWrapper>{estimatedROI}</StatWrapper>
          <StatWrapper>{clickRate}</StatWrapper>
        </FlexBox>
      </>
    );
  } else if (!showFundraisingFields && showClicks) {
    return (
      <>
        <FlexBox justifyContent="space-between">
          <StatWrapper>{replyRate}</StatWrapper>
          <StatWrapper>{filterRate}</StatWrapper>
          <StatWrapper>{optOutRate}</StatWrapper>
          <StatWrapper>{clickRate}</StatWrapper>
        </FlexBox>
      </>
    );
  } else {
    return (
      <>
        <FlexBox justifyContent="space-between">
          <StatWrapper>{replyRate}</StatWrapper>
          <StatWrapper>{filterRate}</StatWrapper>
          <StatWrapper>{optOutRate}</StatWrapper>
        </FlexBox>
      </>
    );
  }
}
